import * as React from "react";
import { isUserRestricted } from "@app/authorization";
import { DeploymentEnvironmentConstants } from "@app/shared/constants";
import { GuestLogin } from "@app/user/guestLogin";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { removeLocalStorageRunConfigurationIds } from "@app/velvet/helpers";
import { JSX } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import AuthenticationService from "@app/authentication/authenticationService";

interface TopBarProps {
  showAside: boolean;
  onHamburgerClick: () => void;
}

function TopBar(props: TopBarProps): JSX.Element {

  const home = "/app/v2.0";

  let barStyling: React.CSSProperties;
  switch (window.environment) {
    case DeploymentEnvironmentConstants.SIT:
    case DeploymentEnvironmentConstants.UAT:
      barStyling = { backgroundColor: "#77bffa" };
      break;
    case DeploymentEnvironmentConstants.DEV:
      barStyling = { backgroundColor: "#a30620" };
      break;
    case DeploymentEnvironmentConstants.Production:
    case DeploymentEnvironmentConstants.Local:
    default:
      barStyling = null;
      break;
  }

  return (
    <header className="app-header app-header-light">
      <div className="top-bar" style={barStyling}>
        <div className="top-bar-brand">
          <a href={home}>
            <img id="logo" role="img" src={`${window.customUIBaseUrl}/images/QuestAnalyticsLogo.svg`} alt="Quest Analytics" style={{ height: "52px", padding: ".25em", width: "auto", }} />
          </a>
        </div>

        <div className="top-bar-list">
          <div className="top-bar-item px-2 d-md-none d-lg-none d-xl-none">
            <QESButton className="hamburger hamburger-squeeze text-dark p-0" aria-label="Toggle Menu" onClick={props.onHamburgerClick} qesType="secondary" qesTestId="top-bar__open-mobile-nav" aria-pressed={props.showAside}>
              <span className="hamburger-box"><span className="hamburger-inner" /></span>
            </QESButton>
          </div>

          <a href={home} className="d-md-none top-bar-mobile-logo">
            <img id="logo" role="img" src={`${window.customUIBaseUrl}/images/QuestAnalyticsLogo.svg`} alt="Quest Analytics" style={{ height: "52px", padding: ".25em", width: "auto", }} />
          </a>

          <UserSection />
        </div>
      </div>
    </header>
  );
}

function UserSection() {
  const user = React.useContext(UserContext);

  const handleLoginClick = React.useCallback(async () => {
    const authService = AuthenticationService.getInstance();
    await authService.login();
  }, []);

  const handleLogoutClick = React.useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    const authService = AuthenticationService.getInstance();
    removeLocalStorageRunConfigurationIds();
    await authService.logout("UserInitiated");
  }, []);

  if (!user) {
    return (
      <div className="top-bar-item top-bar-item-right px-0 d-none d-sm-flex">
        <button className="btn-account" onClick={handleLoginClick}><span className="dropdown-icon"><QesIcon iconName={QesIcons.LogInV1} description="Log in" /></span> Login</button>
      </div>
    );
  }

  const logout = (
    <li><a className="dropdown-item" href="#" onClick={handleLogoutClick}><span className="dropdown-icon"><QesIcon iconName={QesIcons.LogOutV1} description="Log out" /></span> Logout</a></li>
  );
  const encodedUsername = encodeURIComponent(user.name);
  const encodedReturnUrl = encodeURIComponent(window.location.href);
  const isPrimaryAccount = user.isPrimaryAccount;
  const menuItems = !isPrimaryAccount
    ? (<></>)
    : (
      <RoleRestricted deniedRoles={[Roles.SOFTWARE_LICENSE, Roles.TOKEN_USER]}>
        <li>
          <a className="dropdown-item" href={`${window.customUIBaseUrl}/account/manage/profile`}>
            <span className="dropdown-icon"><QesIcon iconName={QesIcons.UserV1} description="User" /></span>
            Profile
          </a>
        </li>
        <li>
          <a className="dropdown-item" href={`${window.authEndpoint}/app/account/change-password/${encodedUsername}?returnUrl=${encodedReturnUrl}`}>
            <span className="dropdown-icon"><QesIcon iconName={QesIcons.SwitchHorizontalV1} description="Change" /></span>
            Change Password
          </a>
        </li>
        <li>
          <a className="dropdown-item" href={`${window.authEndpoint}/app/account/change-username?returnUrl=${encodedReturnUrl}`}>
            <span className="dropdown-icon"><QesIcon iconName={QesIcons.SwitchHorizontalV1} description="Change" /></span>
            Change Username
          </a>
          <div className="dropdown-divider" />
        </li>
      </RoleRestricted>
    );
  const userTile = (
    <span className="user-tile">{user.displayName.charAt(0).toLocaleUpperCase()}</span>
  );
  const accountDescription = isUserRestricted([], [Roles.TOKEN_USER], user)
    ? ""
    : isPrimaryAccount
      ? user.name
      : user.guestLoginInfo.currentAccount.guestClientName;

  return (
    <div className="top-bar-item top-bar-item-right px-0 d-flex">
      <GuestLoginSection />
      <div className="dropdown">
        <button className="btn-account d-flex" type="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          {userTile}{" "}
          <span className="account-summary pe-lg-4 d-none d-lg-block">
            <span className="account-name">{user.displayName}</span>
            <span className="account-description">{accountDescription}</span>
          </span>
        </button>
        <div className="dropdown-arrow dropdown-arrow-left" />
        <div className="dropdown-menu">
          <h6 className="dropdown-header d-none d-md-block d-lg-none">
            {user.displayName}
          </h6>
          <ul className="list-unstyled mb-0">
            {menuItems}
            {logout}
          </ul>
        </div>
      </div>
    </div>
  );
}

function GuestLoginSection() {
  const user = React.useContext(UserContext);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  if (!user.guestLoginInfo.hasGuestAccounts) {
    return (<></>);
  }

  const logins: GuestLogin[] = user.guestLoginInfo.currentAccount.guestUserId === user.guestLoginInfo.parentAccount.guestUserId
    ? [user.guestLoginInfo.parentAccount, ...user.guestLoginInfo.guestAccounts]
    : [user.guestLoginInfo.parentAccount, user.guestLoginInfo.currentAccount, ...user.guestLoginInfo.guestAccounts];

  const selectedValue = user.guestLoginInfo.currentAccount.guestUserId;

  const handleSwitchClick = React.useCallback(async (userId: string) => {
    await AuthenticationService.getInstance().removeUser();

    window.location.href = `${window.authEndpoint}/app/account/switch-login?guestUserId=${userId}&redirectUrl=${window.customUIAbsolutePath}`;

  }, []);

  return (
    <div className="btn-group">
      <QESButton
        className="btn text-gray-dark min-width-zero"
        data-bs-toggle="tooltip"
        data-bs-placement="bottom"
        title="Switch&nbsp;Client"
        aria-label="Switch Client"
        onClick={() => setIsOpen(!isOpen)}
        qesType="secondary"
        qesTestId="top-bar__switch-client"
        aria-pressed={isOpen}
      >
        <QesIcon iconName={QesIcons.SwitchHorizontalV1} description="toggle change clients dropdown" />
      </QESButton>
      {(isOpen || null) && (
        <form id="switchLoginForm" className="inline" method="post">
          <QESSelect
            label="Switch Client"
            hideLabel={true}
            className="form-control bg-white"
            name="userId"
            value={selectedValue}
            onChange={(e) => handleSwitchClick(e.currentTarget.value)}
            testId="switch-client-dropdown"
          >
            {
              logins.map((guestAccount) => {
                const key = guestAccount.guestUserId;
                return (
                  <option key={key} value={key}>
                    {guestAccount.guestClientName}
                  </option>
                );
              })
            }
          </QESSelect>
        </form>
      )}
    </div >
  );
}

export default TopBar;
